import React, { useState, useMemo, useRef } from 'react';
import { t } from 'i18next';
import axios from 'axios';
import DataTable, { ColDef } from '../util/DataTable';
import PageToolbar from '../util/PageToolbar';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from '../util/SearchFilter';
import EmptyState from '../util/EmptyState';
import SidePanel from '../util/SidePanel';
import ProfileCard from '../modal/ProfileCard';
import InviteUserModal from '../modal/InviteUserModal';
import UserAvatar from '../util/UserAvatar';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  second_name?: string;
  job_title?: string;
  email?: string;
  avatarUrl?: string;
  isActive?: boolean;
  role?: { id: number; title: string };
  departmentId?: number;
  supervisorId?: number;
  workingStatus?: { id: number; name: string; color: string; emoji: string } | null;
  pattern?: { name: string; hoursPerWeek?: number };
  mobile_phone?: string;
  city?: string;
}

interface Props {
  users: User[];
  departments: any[];
  deptMemberships: any[];
  baseUrl: string;
  access: any;
  onRefresh?: () => void;
}


const chipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '2px 8px', borderRadius: 20,
  background: 'var(--btn-user-form)', border: 'var(--border-input)',
  fontSize: 10, fontWeight: 600, color: 'var(--text-color-calendar)',
  maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

type ViewMode = 'cards' | 'list';

const EmployeesTab: React.FC<Props> = ({ users, departments, deptMemberships, baseUrl, access, onRefresh }) => {
  const [view, setView] = useState<ViewMode>('list');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [profileUserId, setProfileUserId] = useState<number | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const toolbarSlot = useRef<HTMLDivElement>(null);
  const canAddUser = access?.users_add === true || access?.users_add === 1;

  const supervisorOptions = useMemo(() => {
    const seen = new Set<number>();
    return users.reduce((acc: { value: string; label: string }[], u) => {
      if (u.supervisorId && !seen.has(u.supervisorId)) {
        seen.add(u.supervisorId);
        const sup = users.find(x => x.id === u.supervisorId);
        if (sup) acc.push({ value: String(sup.id), label: `${sup.first_name} ${sup.last_name}` });
      }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  /* ── DataTable columns (List + Table views) ── */
  const baseColumns: ColDef<any>[] = [
    {
      key: 'avatar', label: '', width: '50px', sortable: false, hideable: false,
      render: (row: any) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <UserAvatar user={row} baseUrl={baseUrl} size={32} />
        </div>
      ),
    },
    {
      key: 'name', label: t('Name') || 'Name', sortable: true,
      sortExtract: (row: any) => `${row.first_name} ${row.last_name}`,
      filterType: 'text',
      filterExtract: (row: any) => `${row.first_name} ${row.last_name} ${row.email ?? ''}`,
      render: (row: any) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>{row.first_name} {row.last_name}</div>
          {row.job_title && <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{row.job_title}</div>}
        </div>
      ),
    },
    {
      key: 'email', label: 'Email', sortable: true,
      sortExtract: (row: any) => row.email ?? '',
      render: (row: any) => <span style={{ fontSize: 12, color: 'var(--text-color-calendar)' }}>{row.email}</span>,
    },
    {
      key: 'role', label: t('Role') || 'Role', sortable: true,
      sortExtract: (row: any) => row.role?.title ?? '',
      render: (row: any) => row.role?.title ? (
        <span className="sbadge" style={{ background: 'var(--btn-user-form)', color: 'var(--text-color-calendar)' }}>{row.role.title}</span>
      ) : null,
    },
    {
      key: 'status', label: t('Status') || 'Status', sortable: false,
      filterType: 'boolean',
      filterExtract: (row: any) => String(row.isActive !== false),
      filterOptions: [{ value: 'true', label: t('Active') || 'Active' }, { value: 'false', label: t('Inactive') || 'Inactive' }],
      render: (row: any) => (
        <span className={`sbadge ${row.isActive !== false ? 'fulfilled' : 'rejected'}`}>
          {row.isActive !== false ? (t('Active') || 'Active') : (t('Inactive') || 'Inactive')}
        </span>
      ),
    },
  ];

  const tableColumns: ColDef<any>[] = [
    ...baseColumns,
    {
      key: 'department', label: t('Department') || 'Department', sortable: true,
      sortExtract: (row: any) => departments.find((d: any) => d.id === row.departmentId)?.title ?? '',
      filterType: 'select',
      filterOptions: departments.map((d: any) => ({ value: String(d.id), label: d.title })),
      filterExtract: (row: any) => row.departmentId ? String(row.departmentId) : '',
      render: (row: any) => {
        const dept = departments.find((d: any) => d.id === row.departmentId);
        return dept ? <span style={{ fontSize: 12, color: 'var(--text-color-calendar)' }}>{dept.title}</span> : null;
      },
    },
    {
      key: 'supervisor', label: t('Supervisor') || 'Supervisor', sortable: true,
      sortExtract: (row: any) => {
        const sup = users.find((u: any) => u.id === row.supervisorId);
        return sup ? `${sup.first_name} ${sup.last_name}` : '';
      },
      filterType: 'select',
      filterOptions: supervisorOptions,
      filterExtract: (row: any) => row.supervisorId ? String(row.supervisorId) : '',
      render: (row: any) => {
        const sup = users.find((u: any) => u.id === row.supervisorId);
        return sup ? <span style={{ fontSize: 12, color: 'var(--text-color-calendar)' }}>{sup.first_name} {sup.last_name}</span> : null;
      },
    },
    {
      key: 'hoursPerWeek', label: t('Hours') || 'h/wk', sortable: true, width: '70px',
      sortExtract: (row: any) => row.pattern?.hoursPerWeek ?? 0,
      render: (row: any) => {
        const h = row.pattern?.hoursPerWeek ?? 0;
        return <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{h}h</span>;
      },
    },
    {
      key: 'pattern', label: t('Pattern') || 'Pattern', sortable: true,
      sortExtract: (row: any) => row.pattern?.name ?? '',
      filterType: 'text' as const,
      filterExtract: (row: any) => row.pattern?.name ?? '',
      render: (row: any) => row.pattern?.name
        ? <span style={{ fontSize: 12, color: 'var(--text-color-calendar)' }}>{row.pattern.name}</span>
        : null,
    },
  ];

  const baseUsers = activeOnly ? (users as any[]).filter((u: any) => u.isActive !== false) : (users as any[]);
  const filtered = applySearchAndFilters(baseUsers, search, filters, tableColumns,
    (u: any) => [`${u.first_name} ${u.last_name}`, u.email, u.job_title, u.pattern?.name].filter(Boolean).join(' '));

  const VIEWS: { value: ViewMode; icon: React.ReactNode; label: string }[] = [
    {
      value: 'list',
      label: t('ListView') || 'List',
      icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="12" y1="3" x2="12" y2="21"/></svg>,
    },
    {
      value: 'cards',
      label: t('OrgCardsView') || 'Cards',
      icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      <PageToolbar>
        {/* Search */}
        <SearchFilter grow columns={tableColumns} search={search} onSearch={setSearch} filters={filters} onFilters={setFilters} />

        {/* Тумблер «Только активные» (по умолчанию вкл; выкл → все, включая неактивных) */}
        <div onClick={() => setActiveOnly(v => !v)} title={t('HrActiveOnly') || 'Active only'}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color-calendar)' }}>{t('HrActiveOnly') || 'Active only'}</span>
          <div style={{ width: 34, height: 18, borderRadius: 20, background: activeOnly ? '#10b981' : 'var(--bg-disable)', position: 'relative', transition: 'background .18s', flexShrink: 0 }}>
            <div style={{ position: 'absolute', top: 2, left: activeOnly ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left .18s', boxShadow: '0 1px 3px rgba(0,0,0,.25)' }} />
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {/* Invite user */}
        {canAddUser && (
          <button type="button" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }} onClick={() => setInviteOpen(true)}>
            ✉️ <span>{t('InviteUser') || 'Invite user'}</span>
          </button>
        )}

        {/* DataTable controls slot */}
        {view === 'list' && (
          <div ref={toolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
        )}

        {/* View toggle */}
        <div style={{ display: 'flex', gap: 3 }}>
          {VIEWS.map(v => (
            <button
              key={v.value}
              type="button"
              title={v.label}
              className={`dt-btn-ghost${view === v.value ? ' active' : ''}`}
              onClick={() => setView(v.value)}
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {v.icon}
              <span style={{ fontSize: 11 }}>{v.label}</span>
            </button>
          ))}
        </div>
      </PageToolbar>

      {/* ── Cards ── */}
      {view === 'cards' && (
        <div className="section-scroll">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }}>
          {filtered.map(user => (
            <div
              key={user.id}
              className="ts-card"
              style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, textAlign: 'center', cursor: 'pointer', transition: 'box-shadow .15s' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 14px rgba(0,0,0,.08)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.boxShadow = 'none'}
              onClick={() => setProfileUserId(user.id)}
            >
              <div style={{ position: 'relative' }}>
                <UserAvatar user={user} baseUrl={baseUrl} size={52} />
                {user.workingStatus && (
                  <div title={user.workingStatus.name} style={{ position: 'absolute', bottom: -2, right: -2, width: 13, height: 13, borderRadius: '50%', background: user.workingStatus.color || '#10b981', border: '2px solid var(--bg-form)', fontSize: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {user.workingStatus.emoji}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', lineHeight: 1.3 }}>{user.first_name} {user.last_name}</div>
                {user.job_title && <div style={{ fontSize: 11, color: 'var(--text-color-calendar)', marginTop: 2 }}>{user.job_title}</div>}
              </div>
              {/* Чипы: роль · отдел · паттерн */}
              {(() => {
                const deptName = departments.find((d: any) => d.id === user.departmentId)?.title;
                const hasChips = user.role?.title || deptName || user.pattern?.name;
                if (!hasChips) return null;
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, justifyContent: 'center' }}>
                    {user.role?.title && <span style={chipStyle}>{user.role.title}</span>}
                    {deptName && <span style={chipStyle}>🏢 {deptName}</span>}
                    {user.pattern?.name && (
                      <span style={chipStyle}>🕒 {user.pattern.name}{user.pattern.hoursPerWeek ? ` · ${user.pattern.hoursPerWeek}${t('HShort') || 'h'}` : ''}</span>
                    )}
                  </div>
                );
              })()}
              {user.mobile_phone && (
                <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  📞 {user.mobile_phone}
                </div>
              )}
              {user.email && (
                <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                  {user.email}
                </div>
              )}
              {user.isActive === false && (
                <span className="sbadge rejected" style={{ fontSize: 9 }}>Inactive</span>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1/-1' }}>
              <EmptyState message={t('NoEmployeesFound') || 'No employees found'} />
            </div>
          )}
        </div>
        </div>
      )}

      {/* ── List (DataTable with full column set) ── */}
      {view === 'list' && (
        <div className="section-scroll">
          <DataTable
            rows={filtered as any[]}
            columns={tableColumns}
            filterable={false}
            getKey={(row: any) => row.id}
            storageKey="employees-list-view"
            toolbarRef={toolbarSlot}
            onRowClick={(row: any) => setProfileUserId(row.id)}
          />
        </div>
      )}

      <SidePanel open={profileUserId != null} onClose={() => setProfileUserId(null)} width={760}>
        {profileUserId != null && (() => {
          const pu = (users as any[]).find((u: any) => u.id === profileUserId) || { id: profileUserId, first_name: '', last_name: '' };
          return (
            <ProfileCard
              isSelf={false}
              hrMode
              selectedUser={{ id: pu.id, first_name: pu.first_name, last_name: pu.last_name }}
              users={users as any[]}
              access={access}
              baseUrl={baseUrl}
              setShow={(v) => { if (!v) setProfileUserId(null); }}
              refreshUserHeder={() => {}}
              refreshUsers={() => { onRefresh?.(); }}
            />
          );
        })()}
      </SidePanel>

      {inviteOpen && (
        <InviteUserModal
          onClose={() => setInviteOpen(false)}
          onInvited={() => onRefresh?.()}
        />
      )}
    </div>
  );
};

export default EmployeesTab;
